import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/config';
import { useAuthSession } from './AuthSessionContext';

const EmergencyEventContext = createContext(null);
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;

class AuthenticationError extends Error {}

const firstValue = (...values) => values.find(
    (value) => value !== undefined && value !== null && value !== ''
);

const asObject = (value) => (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const parseJson = (value) => {
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch (error) {
        return value;
    }
};

export const canonicalEventType = (value) => String(value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();

export const parseSseBlock = (block) => {
    let event = 'message';
    let id = null;
    const data = [];

    block.split(/\r\n|\r|\n/).forEach((line) => {
        if (!line || line.startsWith(':')) return;

        const colonIndex = line.indexOf(':');
        const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
        let value = colonIndex === -1 ? '' : line.slice(colonIndex + 1);
        if (value.startsWith(' ')) value = value.slice(1);

        if (field === 'event') event = value || 'message';
        if (field === 'id' && !value.includes('\0')) id = value;
        if (field === 'data') data.push(value);
    });

    if (data.length === 0) return null;
    return { event, id, data: data.join('\n') };
};

export const createSseParser = (onMessage) => {
    let buffer = '';

    const drain = () => {
        let separator = buffer.match(/\r\n\r\n|\n\n|\r\r/);
        while (separator) {
            const block = buffer.slice(0, separator.index);
            buffer = buffer.slice(separator.index + separator[0].length);
            const message = parseSseBlock(block);
            if (message) onMessage(message);
            separator = buffer.match(/\r\n\r\n|\n\n|\r\r/);
        }
    };

    return {
        push(chunk) {
            buffer += chunk;
            drain();
        },
        flush() {
            if (buffer.trim()) {
                const message = parseSseBlock(buffer);
                if (message) onMessage(message);
            }
            buffer = '';
        }
    };
};

const mergedPayload = (raw) => {
    const parsed = parseJson(raw);
    const outer = asObject(parsed);
    const nested = asObject(outer.data);
    const nestedEvent = asObject(outer.event);
    return { ...outer, ...nestedEvent, ...nested };
};

export const extractEmergencyEventId = (raw, fallbackId = null) => {
    const source = mergedPayload(raw);
    return firstValue(
        source.id,
        source.eventId,
        source.event_id,
        source.emergencyEventId,
        source.emergency_event_id,
        source.emergencyId,
        source.emergency_id,
        fallbackId
    );
};

export const normalizeEmergencyEvent = (raw, fallbackId = null) => {
    const source = mergedPayload(raw);
    const id = extractEmergencyEventId(source, fallbackId);
    if (id === undefined || id === null || id === '') return null;

    return {
        id,
        appUserId: firstValue(
            source.appUserId,
            source.app_user_id,
            source.userId,
            source.user_id,
            asObject(source.appUser).id,
            asObject(source.app_user).id
        ),
        userName: firstValue(
            source.userName,
            source.user_name,
            source.appUserName,
            source.app_user_name,
            source.name
        ),
        message: firstValue(
            source.message,
            source.alertMessage,
            source.alert_message,
            source.reason,
            source.description,
            source.title
        ),
        occurredAt: firstValue(
            source.occurredAt,
            source.occurred_at,
            source.createdAt,
            source.created_at,
            source.eventAt,
            source.event_at
        ),
        raw: source
    };
};

export const extractUnreadEvents = (payload) => {
    if (Array.isArray(payload)) return payload;

    const source = asObject(payload);
    const data = source.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(source.events)) return source.events;
    if (Array.isArray(source.unreadEvents)) return source.unreadEvents;
    if (Array.isArray(source.unread_events)) return source.unread_events;
    if (Array.isArray(asObject(data).events)) return data.events;
    if (Array.isArray(asObject(data).unreadEvents)) return data.unreadEvents;
    if (Array.isArray(asObject(data).unread_events)) return data.unread_events;
    return [];
};

const readResponseJson = async (response) => {
    const text = await response.text();
    return text ? parseJson(text) : null;
};

const emergencyEventTime = (event) => {
    if (!event.occurredAt) return Number.POSITIVE_INFINITY;
    const timestamp = new Date(event.occurredAt).getTime();
    return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
};

export const compareEmergencyEvents = (left, right) => {
    const leftTime = emergencyEventTime(left);
    const rightTime = emergencyEventTime(right);
    if (leftTime !== rightTime) return leftTime - rightTime;
    return (left.receivedSequence || 0) - (right.receivedSequence || 0);
};

const ensureAuthorizedResponse = (response) => {
    if (response.status === 401 || response.status === 403) {
        throw new AuthenticationError('Authentication expired.');
    }
    return response;
};

const consumeSseStream = async (body, onMessage) => {
    if (!body || typeof body.getReader !== 'function') {
        throw new Error('The emergency event stream is not readable.');
    }

    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = createSseParser(onMessage);

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
    }

    parser.push(decoder.decode());
    parser.flush();
};

export const EmergencyEventProvider = ({ children }) => {
    const { token, isCenterAdmin, logout } = useAuthSession();
    const navigate = useNavigate();
    const [queue, setQueue] = useState([]);
    // react-router 의 navigate 는 경로가 바뀔 때마다 새 함수라서, 스트림 effect 의
    // 의존성에 넣으면 페이지를 이동할 때마다 SSE 를 끊고 다시 연다. 최신 값은
    // ref 로 들고 있고 effect 는 token / isCenterAdmin 이 바뀔 때만 다시 연결한다.
    const navigateRef = useRef(navigate);
    const logoutRef = useRef(logout);
    useEffect(() => {
        navigateRef.current = navigate;
        logoutRef.current = logout;
    }, [navigate, logout]);
    const seenIdsRef = useRef(new Set());
    const acknowledgedIdsRef = useRef(new Set());
    const receivedSequenceRef = useRef(0);

    const removeEvent = useCallback((eventId) => {
        if (eventId === undefined || eventId === null) return;
        const key = String(eventId);
        acknowledgedIdsRef.current.add(key);
        setQueue((currentQueue) => currentQueue.filter(
            (event) => String(event.id) !== key
        ));
    }, []);

    const enqueueEvent = useCallback((rawEvent, fallbackId = null) => {
        const event = normalizeEmergencyEvent(rawEvent, fallbackId);
        if (!event) return;

        const key = String(event.id);
        if (seenIdsRef.current.has(key) || acknowledgedIdsRef.current.has(key)) return;

        seenIdsRef.current.add(key);
        receivedSequenceRef.current += 1;
        const queuedEvent = {
            ...event,
            receivedSequence: receivedSequenceRef.current
        };
        setQueue((currentQueue) => (
            [...currentQueue, queuedEvent].sort(compareEmergencyEvents)
        ));
    }, []);

    const handleSseMessage = useCallback((message) => {
        const payload = parseJson(message.data);
        const outerPayload = asObject(payload);
        const source = mergedPayload(payload);
        const type = canonicalEventType(firstValue(
            message.event !== 'message' ? message.event : null,
            typeof outerPayload.event === 'string' ? outerPayload.event : null,
            source.type,
            source.eventType,
            source.event_type
        ));

        if (type === 'emergency_ack') {
            removeEvent(extractEmergencyEventId(source, message.id));
            return;
        }

        if (type === 'emergency_alert' || type === 'emergency_event') {
            enqueueEvent(source, message.id);
        }
    }, [enqueueEvent, removeEvent]);

    useEffect(() => {
        seenIdsRef.current.clear();
        acknowledgedIdsRef.current.clear();
        receivedSequenceRef.current = 0;
        setQueue([]);
    }, [token]);

    useEffect(() => {
        if (!token || !isCenterAdmin) return undefined;

        let cancelled = false;
        let retryTimer = null;
        let retryAttempt = 0;
        let activeController = null;
        let unauthorizedHandled = false;

        const handleUnauthorized = () => {
            if (unauthorizedHandled || cancelled) return;
            unauthorizedHandled = true;
            logoutRef.current();
            navigateRef.current('/doctor', { replace: true });
        };

        const scheduleReconnect = (connect) => {
            if (cancelled) return;
            const delay = Math.min(
                INITIAL_RETRY_DELAY_MS * (2 ** retryAttempt),
                MAX_RETRY_DELAY_MS
            );
            retryAttempt += 1;
            retryTimer = window.setTimeout(connect, delay);
        };

        const connect = async () => {
            const controller = new AbortController();
            const connectedAt = Date.now();
            activeController = controller;
            const headers = {
                Accept: 'text/event-stream',
                Authorization: `Bearer ${token}`
            };

            try {
                const streamResponse = ensureAuthorizedResponse(await fetch(
                    `${API_BASE_URL}/api/emergency-events/stream`,
                    { method: 'GET', headers, signal: controller.signal }
                ));

                if (!streamResponse.ok) {
                    throw new Error(`Emergency stream failed (${streamResponse.status}).`);
                }

                // Rebuild from the authoritative unread snapshot after every reconnect.
                seenIdsRef.current.clear();
                setQueue([]);

                const streamTask = consumeSseStream(streamResponse.body, (message) => {
                    if (!cancelled && activeController === controller) {
                        retryAttempt = 0;
                        handleSseMessage(message);
                    }
                });

                // Start catch-up only after the stream HTTP connection is established.
                const unreadTask = (async () => {
                    const unreadResponse = ensureAuthorizedResponse(await fetch(
                        `${API_BASE_URL}/api/emergency-events/unread`,
                        { method: 'GET', headers, signal: controller.signal }
                    ));

                    if (!unreadResponse.ok) {
                        throw new Error(`Unread emergency events failed (${unreadResponse.status}).`);
                    }

                    const unreadPayload = await readResponseJson(unreadResponse);
                    if (!cancelled && activeController === controller) {
                        extractUnreadEvents(unreadPayload).forEach((event) => enqueueEvent(event));
                    }
                })();

                await Promise.all([streamTask, unreadTask]);
                throw new Error('Emergency event stream ended.');
            } catch (error) {
                controller.abort();
                if (cancelled || error?.name === 'AbortError') return;
                if (error instanceof AuthenticationError) {
                    handleUnauthorized();
                    return;
                }
                if (Date.now() - connectedAt >= MAX_RETRY_DELAY_MS) {
                    retryAttempt = 0;
                }
                scheduleReconnect(connect);
            }
        };

        connect();

        return () => {
            cancelled = true;
            if (retryTimer) window.clearTimeout(retryTimer);
            if (activeController) activeController.abort();
        };
    }, [enqueueEvent, handleSseMessage, isCenterAdmin, token]);

    const acknowledgeEvent = useCallback(async (eventId) => {
        if (!token || eventId === undefined || eventId === null) {
            throw new Error('응급 이벤트를 확인할 수 없습니다.');
        }

        let response;
        try {
            response = ensureAuthorizedResponse(await fetch(
                `${API_BASE_URL}/api/emergency-events/${encodeURIComponent(eventId)}/ack`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            ));
        } catch (error) {
            if (error instanceof AuthenticationError) {
                logout();
                navigate('/doctor', { replace: true });
                throw new Error('로그인이 만료되었습니다. 다시 로그인해주세요.');
            }
            throw error;
        }

        if (response.status === 404 || response.status === 409) {
            removeEvent(eventId);
            return null;
        }

        if (!response.ok) {
            const payload = await readResponseJson(response);
            throw new Error(
                asObject(payload).message
                || asObject(asObject(payload).error).message
                || `응급 이벤트 확인에 실패했습니다. (${response.status})`
            );
        }

        const payload = await readResponseJson(response);
        removeEvent(eventId);
        const acknowledgedId = extractEmergencyEventId(payload);
        if (acknowledgedId !== undefined && acknowledgedId !== null) {
            removeEvent(acknowledgedId);
        }
        return payload;
    }, [logout, navigate, removeEvent, token]);

    const value = useMemo(() => ({
        currentEvent: queue[0] || null,
        pendingCount: queue.length,
        acknowledgeEvent
    }), [acknowledgeEvent, queue]);

    return (
        <EmergencyEventContext.Provider value={value}>
            {children}
        </EmergencyEventContext.Provider>
    );
};

export const useEmergencyEvents = () => {
    const context = useContext(EmergencyEventContext);
    if (!context) {
        throw new Error('useEmergencyEvents must be used within an EmergencyEventProvider');
    }
    return context;
};
